import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, toDisplayString as _toDisplayString, createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, createBlock as _createBlock, withKeys as _withKeys, createElementVNode as _createElementVNode } from "vue"

const _hoisted_1 = { class: "username-form" }
const _hoisted_2 = ["innerHTML"]
const _hoisted_3 = {
  key: 2,
  class: "username-form__display-name"
}
const _hoisted_4 = { class: "login-info" }

import type { NextcloudUser } from '@nextcloud/auth'

import { getGuestNickname } from '@nextcloud/auth'
import { subscribe, unsubscribe } from '@nextcloud/event-bus'
import { t } from '@nextcloud/l10n'
import { generateUrl } from '@nextcloud/router'
import debounce from 'debounce'
import escapeHtml from 'escape-html'
import { computed, nextTick, onBeforeUnmount, ref, useTemplateRef, watch } from 'vue'
import NcButton from '@nextcloud/vue/components/NcButton'
import NcTextField from '@nextcloud/vue/components/NcTextField'
import IconAccountOutline from 'vue-material-design-icons/AccountOutline.vue'
import IconPencilOutline from 'vue-material-design-icons/PencilOutline.vue'
import { useGetToken } from '../composables/useGetToken.ts'
import { EventBus } from '../services/EventBus.ts'
import { useActorStore } from '../stores/actor.ts'
import { useGuestNameStore } from '../stores/guestName.js'


export default /*@__PURE__*/_defineComponent({
  __name: 'SetGuestUsername',
  props: {
    compact: { type: Boolean, default: false }
  },
  emits: ["update"],
  setup(__props: any, { emit: __emit }) {



const emit = __emit
const loginUrl = `${generateUrl('/login')}?redirect_url=${encodeURIComponent(window.location.pathname)}`

const actorStore = useActorStore()
const guestNameStore = useGuestNameStore()
const token = useGetToken()

const usernameInput = useTemplateRef('usernameInput')

const guestUserName = ref(getGuestNickname() || '')
const isEditingUsername = ref(false)

const actorDisplayName = computed<string>(() => actorStore.displayName || guestUserName.value)
const displayNameLabel = computed(() => t('spreed', 'Display name: {name}', {
	name: `<strong>${escapeHtml(actorDisplayName.value)}</strong>`,
}, { escape: false }))
const debounceUpdateDisplayName = debounce(updateDisplayName, 500)

watch(actorDisplayName, (newValue) => {
	guestUserName.value = newValue
})

/** Initially set displayName in store, if available from BrowserStorage */
let delayUpdateDisplayName = false
if (guestUserName.value && !actorStore.displayName) {
	actorStore.setDisplayName(guestUserName.value)
	delayUpdateDisplayName = true
}

/** Update guest displayName for other attendees */
EventBus.once('joined-conversation', () => {
	if (guestUserName.value && delayUpdateDisplayName) {
		console.debug('Saving guest name from browser storage to the session')
		updateDisplayName()
	}
})

/** Update guest displayName from public event (e.g. @nextcloud/auth) */
subscribe('user:info:changed', updateDisplayNameFromPublicEvent)
onBeforeUnmount(() => {
	unsubscribe('user:info:changed', updateDisplayNameFromPublicEvent)
	updateDisplayName()
})

/**
 * Update guest username from public page user menu
 *
 * @param payload
 */
function updateDisplayNameFromPublicEvent(payload: NextcloudUser) {
	if (payload.displayName && payload.displayName !== guestUserName.value) {
		guestUserName.value = payload.displayName
		updateDisplayName()
	}
}

/** Set guest username locally and send request to server to update for other attendees */
function updateDisplayName() {
	if (!guestUserName.value) {
		return
	}
	guestNameStore.submitGuestUsername(token.value, guestUserName.value)
	isEditingUsername.value = false
}

/** Toggle editing state of username */
function toggleEdit() {
	isEditingUsername.value = !isEditingUsername.value
	if (isEditingUsername.value) {
		nextTick(() => {
			usernameInput.value!.focus()
		})
	}
}

// One-way binding to parent component
watch(guestUserName, (newValue) => {
	debounceUpdateDisplayName()
	emit('update', newValue)
}, { immediate: true })

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (!__props.compact)
      ? (_openBlock(), _createElementBlock("h3", {
          key: 0,
          innerHTML: displayNameLabel.value
        }, null, 8, _hoisted_2))
      : _createCommentVNode("", true),
    (!isEditingUsername.value && !__props.compact)
      ? (_openBlock(), _createBlock(_unref(NcButton), {
          key: 1,
          onClick: toggleEdit
        }, {
          icon: _withCtx(() => [
            _createVNode(IconPencilOutline, { size: 20 })
          ]),
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_unref(t)('spreed', 'Edit display name')) + " ", 1)
          ]),
          _: 1
        }))
      : (_openBlock(), _createElementBlock("div", _hoisted_3, [
          _createVNode(IconAccountOutline, {
            class: "username-form__display-name-icon",
            size: 20
          }),
          _createVNode(_unref(NcTextField), {
            ref_key: "usernameInput",
            ref: usernameInput,
            modelValue: guestUserName.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((guestUserName).value = $event)),
            placeholder: _unref(t)('spreed', 'Guest'),
            class: "username-form__input",
            label: _unref(t)('spreed', 'Display name (required)'),
            "show-trailing-button": !!guestUserName.value && !__props.compact,
            "trailing-button-icon": "arrowEnd",
            "trailing-button-label": _unref(t)('spreed', 'Save name'),
            onTrailingButtonClick: _cache[1] || (_cache[1] = ($event: any) => (!__props.compact ? updateDisplayName() : null)),
            onKeydown: [
              _cache[2] || (_cache[2] = _withKeys(($event: any) => (!__props.compact ? updateDisplayName() : null), ["enter"])),
              _withKeys(toggleEdit, ["esc"])
            ]
          }, null, 8, ["modelValue", "placeholder", "label", "show-trailing-button", "trailing-button-label"])
        ])),
    _createElementVNode("div", _hoisted_4, [
      _createElementVNode("span", null, _toDisplayString(_unref(t)('spreed', 'Do you already have an account?')), 1),
      _createVNode(_unref(NcButton), {
        class: "login-info__button",
        variant: "secondary",
        href: loginUrl
      }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(_unref(t)('spreed', 'Log in')), 1)
        ]),
        _: 1
      })
    ])
  ]))
}
}

})